import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Web3Forms API Key
const WEB3FORMS_KEY = 'daf751c6-556e-4dbb-9758-bc61097855b2';

const Contact = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          name: formData.name,
          email: formData.email,
          company: formData.company || 'Not provided',
          message: formData.message,
          subject: `New Contact from ESOT-EU Website: ${formData.name}`,
          from_name: 'ESOT-EU Website',
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { 
      icon: Mail, 
      label: t('contact.info.email'), 
      value: 'info@esot-eu.com',
    },
    { 
      icon: MapPin, 
      label: t('contact.info.location'), 
      value: 'Bulgaria, EU',
    },
    { 
      icon: Clock, 
      label: t('contact.info.availability'), 
      value: t('contact.info.availabilityText'),
    },
  ];

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0F172A]"></div>
      <div className="absolute inset-0 blueprint-grid opacity-30"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 mb-6"
          >
            <div className="h-px w-12 bg-[#6B8CAE]"></div>
            <span className="text-[#6B8CAE] text-sm font-mono uppercase tracking-wider">
              {t('contact.badge')}
            </span>
            <div className="h-px w-12 bg-[#6B8CAE]"></div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            data-testid="contact-title"
          >
            {t('contact.title')}{' '}
            <span className="text-[#6B8CAE]">{t('contact.titleHighlight')}</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#94A3B8] text-lg max-w-2xl mx-auto"
          >
            {t('contact.subtitle')}
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[#94A3B8] text-sm mb-2 font-mono">
                    {t('contact.form.name')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-technical w-full"
                    placeholder={t('contact.form.name')}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-[#94A3B8] text-sm mb-2 font-mono">
                    {t('contact.form.email')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-technical w-full"
                    placeholder={t('contact.form.email')}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#94A3B8] text-sm mb-2 font-mono">
                  {t('contact.form.company')}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="input-technical w-full"
                  placeholder={t('contact.form.company')}
                  data-testid="contact-company-input"
                />
              </div>

              <div>
                <label className="block text-[#94A3B8] text-sm mb-2 font-mono">
                  {t('contact.form.message')} *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="input-technical w-full resize-none"
                  placeholder={t('contact.form.message')}
                  data-testid="contact-message-input"
                />
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{t('contact.form.sending')}</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>{t('contact.form.submit')}</span>
                  </>
                )}
              </motion.button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center space-x-2 text-green-400 bg-green-400/10 border border-green-400/30 p-4"
                  data-testid="contact-success-message"
                >
                  <CheckCircle size={20} />
                  <span>{t('contact.form.success')}</span>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center space-x-2 text-red-400 bg-red-400/10 border border-red-400/30 p-4"
                  data-testid="contact-error-message"
                >
                  <AlertCircle size={20} />
                  <span>{t('contact.form.error')}</span>
                </motion.div>
              )}
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-8"
          >
            <div className="card-technical p-8">
              <h3 className="text-white text-xl font-semibold mb-4">
                {t('contact.info.title')}
              </h3>
              <p className="text-[#94A3B8] mb-8">
                {t('contact.info.description')}
              </p>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-start space-x-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 flex items-center justify-center flex-shrink-0">
                      <item.icon size={20} className="text-[#6B8CAE]" />
                    </div>
                    <div>
                      <div className="text-[#94A3B8] text-sm font-mono uppercase tracking-wider">
                        {item.label}
                      </div>
                      <div className="text-white mt-1">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="card-technical p-4 h-[200px] relative overflow-hidden">
              <div className="absolute inset-0 bg-[#0F172A] grid-background flex items-center justify-center">
                <div className="text-center">
                  <MapPin size={32} className="text-[#6B8CAE] mx-auto mb-2" />
                  <p className="text-[#94A3B8] text-sm font-mono">Bulgaria, EU</p>
                  <p className="text-[#6B8CAE] text-xs mt-1">European Union</p>
                </div>
              </div>
              
              {/* Decorative Grid Points */}
              <div className="absolute inset-0 pointer-events-none">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-[#6B8CAE]/30 rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
